import { ProcessingJobType } from "@prisma/client";
import { CreditBucket } from "@/app/types/billing/CreditWallet";

// The single source of truth for every price. Credits are sold as a batch of UNIT_SIZE at
// UNIT_PRICE_EUR, and every paid action is priced here, in credits. See docs/Pricing.md for
// the grid and the margins.
export const Credits = {
  UNIT_SIZE: 100,
  UNIT_PRICE_EUR: 39,

  USD_TO_EUR: 0.92,
  MIN_TOPUP_HOURS: 1,
  MAX_TOPUP_HOURS: 100,
  DEFAULT_TOPUP_HOURS: 5,

  MIN_TOPUP_PROSPECTS: 100,
  MAX_TOPUP_PROSPECTS: 5000,
  DEFAULT_TOPUP_PROSPECTS: 250,
  TOPUP_PROSPECTS_STEP: 100,

  FREE_CALL_CREDITS: 60,
  FREE_PROSPECT_CREDITS: 50,

  // Metered by time: a project owes this many credits per started hour of analyzed audio.
  ANALYSIS_CREDITS_PER_HOUR: 30,

  // Metered by volume: how many units one credit buys.
  PROSPECTS_PER_CREDIT: 2,
  IMPORTED_PROFILES_PER_CREDIT: 100,
  EXTENSION_PROFILES_PER_CREDIT: 1,
} as const;

// Flat per-job price, keyed by the job type so the charge and the failure refund both read it.
// CALL_ANALYSIS and IMPORTED_PROFILE_DETECTION are 0 here because they are metered elsewhere
// (per hour, and up-front by the detection flow).
export const ProcessingPrice: Record<ProcessingJobType, number> = {
  [ProcessingJobType.CALL_ANALYSIS]: 0,
  [ProcessingJobType.CLIENT_AVATAR]: 0,
  [ProcessingJobType.PROSPECT_SEARCH]: 0,
  [ProcessingJobType.IMPORTED_PROFILE_DETECTION]: 0,
  [ProcessingJobType.MESSAGE_SEQUENCE]: 0,
  [ProcessingJobType.MESSAGE_SEQUENCE_STEP]: 0,
  [ProcessingJobType.REPLY_SUGGESTION]: 0,
  [ProcessingJobType.CALL_INSIGHT_RECOUNT]: 0,
};

export const Subscription = {
  MONTHLY_PRICE_EUR: 79,
  ANNUAL_DISCOUNT_PERCENT: 20,
  CALL_HOURS_PER_MONTH: 25,
  PROSPECTS_PER_MONTH: 250,
  TOPUP_MARKUP_PERCENT: 25,
} as const;

export const ProcessingBucket: Partial<Record<ProcessingJobType, CreditBucket>> = {
  [ProcessingJobType.CALL_ANALYSIS]: CreditBucket.CALL,
  [ProcessingJobType.CALL_INSIGHT_RECOUNT]: CreditBucket.CALL,
  [ProcessingJobType.IMPORTED_PROFILE_DETECTION]: CreditBucket.PROSPECT,
};

export function analysisSecondsForCredits(credits: number): number {
  return (Math.max(0, credits) / Credits.ANALYSIS_CREDITS_PER_HOUR) * 3600;
}

function hourCredits(seconds: number): number {
  return Math.ceil(Math.max(0, seconds) / 3600) * Credits.ANALYSIS_CREDITS_PER_HOUR;
}

// A sequence generation is billed per variant: one credit for each arm written across every
// step, so 1 -> 1, 2 -> 2, 3 -> 3. Same unit as filling a prospect's messages, one per arm.
export function sequenceGenerationCost(perVariant: number, variantsCount: number): number {
  return perVariant * Math.max(1, variantsCount);
}

// Call analysis is billed per project on a cumulative basis: a project owes ANALYSIS_CREDITS_PER_HOUR
// credits per started hour of analyzed audio, summed across all its analyzed calls. Analyzing a new
// call costs for the number of hour boundaries its duration makes the project total cross, so a short
// call that fits inside the current started hour is free (0 credit).
export function creditsForAddedDuration(
  billedSeconds: number | null | undefined,
  addedSeconds: number | null | undefined,
): number {
  const billed = billedSeconds ?? 0;
  return hourCredits(billed + (addedSeconds ?? 0)) - hourCredits(billed);
}

export function creditsForRemovedDuration(
  billedSeconds: number | null | undefined,
  removedSeconds: number | null | undefined,
): number {
  const billed = billedSeconds ?? 0;
  return hourCredits(billed) - hourCredits(billed - (removedSeconds ?? 0));
}
